import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { CancellationPolicyView } from '../cancellationPolicy';
import { mobileTapTargetSx } from '../bookingMobile';
import { CancellationPolicyPanel } from './CancellationPolicyPanel';

interface Props {
  open: boolean;
  /**
   * La política ya resuelta. Es el MISMO objeto del que sale lo que se dibuja
   * y lo que el botón puede hacer: así no puede quedar un botón rojo activo
   * sobre un diálogo que todavía no dice nada.
   */
  view: CancellationPolicyView;
  /** Motivo por el que no hay política (aviso del backend), si lo hay. */
  notice: string | null;
  isCancelling: boolean;
  /** El operador rechazó la cancelación: por qué. */
  refusal?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirmación de cancelación con la plata delante. Existe porque cancelar es
 * irreversible y el costo no es obvio: con tarifas no reembolsables el
 * operador devuelve 0 — y eso ES la política, no un error. Verlo después de
 * apretar no sirve de nada.
 *
 * El botón rojo NO ejecuta hasta que hay algo dicho sobre la plata: los
 * números que publicó el proveedor, o el aviso honesto de que no los publica.
 * Habilitarlo y rotularlo salen de la MISMA expresión que decide qué se
 * dibuja (`cancellationPolicy.ts`), así que no puede quedar un botón que
 * acepta un cargo sobre un diálogo todavía vacío, ni uno que promete gratis
 * lo que el proveedor no dijo que fuera gratis.
 */
export function CancelBookingDialog({
  open,
  view,
  notice,
  isCancelling,
  refusal,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={isCancelling ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      // Los dos botones de este diálogo son la última decisión del flujo en
      // mobile: el mínimo táctil es el mismo del sheet, de la misma expresión.
      sx={mobileTapTargetSx}
    >
      <DialogTitle>{t('travelServices.detail.cancelConfirmTitle')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <CancellationPolicyPanel view={view} notice={notice} />

          {/* El operador rechazó la cancelación: no es un fallo del sistema, y
              el asesor necesita el texto para explicárselo al pasajero. */}
          {refusal && (
            <Alert severity="warning">
              <AlertTitle>{t('travelServices.detail.cancelRefused')}</AlertTitle>
              {refusal}
            </Alert>
          )}

          <Typography variant="body2">
            {t('travelServices.detail.cancelConfirmQuestion')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCancelling}>
          {t('travelServices.detail.cancelKeep')}
        </Button>
        <LoadingButton
          color="error"
          variant="contained"
          loading={isCancelling}
          disabled={!view.canCancel}
          onClick={onConfirm}
        >
          {t(
            view.acceptsCharge
              ? 'travelServices.detail.cancelConfirm'
              : 'travelServices.detail.cancelConfirmFree',
          )}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
